import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Loader2, Headset, X, LogIn } from 'lucide-react';
import { API_BASE_URL } from '@/utils/apiClient';

const HIDDEN_PATHS = [
  '/login', '/forgot-password', '/phone-register', '/verify-otp', '/verify-phone',
  '/verify-identity', '/complete-profile', '/profile-setup', '/welcome',
  '/signup-driver', '/signup-passenger', '/auth-callback', '/privacidad', '/sara-test',
  // Chats internos conductor↔pasajero: la burbuja tapaba el botón de enviar en móvil
  '/booking-chat', '/route-inquiry', '/chat', '/my-booking-chats',
];

const getGuestId = () => {
  let id = localStorage.getItem('sara_guest_id');
  if (!id || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) {
    id = 'g' + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('sara_guest_id', id);
  }
  return id;
};

export default function SaraBubble() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [humanActive, setHumanActive] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [teaser, setTeaser] = useState(false);
  const bottomRef = useRef(null);

  const token = localStorage.getItem('token');
  const isGuest = !token;

  // Apertura programática (ej. botón "Pedir mi ruta" de la landing)

  // Globito discreto para visitantes: aparece a los 4s con un mensaje que aclara
  // que es una alternativa a WhatsApp (no depende del canal externo). Se cierra
  // solo a los 30s y si el usuario lo descarta, no vuelve a insistir.
  useEffect(() => {
    if (!isGuest || open) return;
    if (localStorage.getItem('sara_teaser_dismissed')) return;
    const show = setTimeout(() => setTeaser(true), 4000);
    const hide = setTimeout(() => setTeaser(false), 34000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, [isGuest, open]);

  const dismissTeaser = (permanent) => {
    setTeaser(false);
    if (permanent) localStorage.setItem('sara_teaser_dismissed', '1');
  };

  const hidden = HIDDEN_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))
    || location.pathname.startsWith('/admin');

  const loadHistory = useCallback(async () => {
    try {
      const t = localStorage.getItem('token');
      const { data } = t
        ? await axios.get(`${API_BASE_URL}/api/sara/history`, { headers: { Authorization: `Bearer ${t}` } })
        : await axios.get(`${API_BASE_URL}/api/sara/guest-history`, { params: { guest_id: getGuestId() } });
      setMessages(data.messages || []);
      setHumanActive(!!data.human_active);
      setRemaining(data.remaining_today);
    } catch (e) { /* silencioso */ }
  }, []);

  // Apertura programática: normal (prefill) o proactiva cuando una búsqueda no tiene resultados
  useEffect(() => {
    const onOpenEvent = (e) => {
      setTeaser(false);
      setOpen(true);
      if (e.detail?.prefill) setInput(e.detail.prefill);
      if (e.detail?.proactiveRoute) {
        const { origin, destination } = e.detail.proactiveRoute;
        const t = localStorage.getItem('token');
        const req = t
          ? axios.post(`${API_BASE_URL}/api/sara/proactive-route`, { origin, destination },
              { headers: { Authorization: `Bearer ${t}` } })
          : axios.post(`${API_BASE_URL}/api/sara/proactive-route`, { origin, destination, guest_id: getGuestId() });
        req.then(({ data }) => {
          if (data.reply && !data.duplicated) {
            setMessages(prev => [...prev, { role: 'assistant', content: data.reply, meta: { type: 'proactive_route' } }]);
          }
          loadHistory();
        }).catch(() => {});
      }
    };
    window.addEventListener('sara:open', onOpenEvent);
    return () => window.removeEventListener('sara:open', onOpenEvent);
  }, [loadHistory]);

  useEffect(() => {
    if (!open) return;
    loadHistory();
    const t = setInterval(loadHistory, 6000);
    return () => clearInterval(t);
  }, [open, loadHistory]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    setMessages(prev => [...prev, { role: 'user', content: text, created_at: new Date().toISOString() }]);
    const t = localStorage.getItem('token');
    const doPost = () => t
      ? axios.post(`${API_BASE_URL}/api/sara/chat`, { message: text },
          { headers: { Authorization: `Bearer ${t}` }, timeout: 90000 })
      : axios.post(`${API_BASE_URL}/api/sara/guest-chat`, { message: text, guest_id: getGuestId() },
          { timeout: 90000 });
    try {
      let data;
      try {
        ({ data } = await doPost());
      } catch (firstErr) {
        await new Promise(r => setTimeout(r, 1500));
        ({ data } = await doPost());
      }
      if (data.reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply, meta: data.meta }]);
      }
      if (data.handled_by === 'human') setHumanActive(true);
      if (typeof data.remaining_today === 'number') setRemaining(data.remaining_today);
    } catch (e) {
      const detail = typeof e.response?.data?.detail === 'string' ? e.response.data.detail : null;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: detail || 'Tuve un problema de conexión y no pude procesar tu mensaje. Por favor envíalo de nuevo.',
      }]);
    } finally {
      setSending(false);
    }
  };

  if (hidden) return null;

  const roleStyle = (role) =>
    role === 'user'
      ? 'bg-blue-600 text-white ml-auto'
      : role === 'agent'
        ? 'bg-emerald-100 text-emerald-900 border border-emerald-200'
        : 'bg-white text-slate-800 border border-slate-200';

  if (!open) {
    return (
      <div className="fixed bottom-24 right-4 md:bottom-6 md:right-24 z-[9998] flex items-end gap-2">
        {teaser && (
          <div
            data-testid="sara-teaser"
            className="relative bg-white text-slate-800 rounded-2xl rounded-br-sm shadow-xl border border-slate-200 px-4 py-3 max-w-[240px] text-sm animate-in fade-in slide-in-from-bottom-2 duration-500"
          >
            <button
              onClick={() => dismissTeaser(true)}
              data-testid="sara-teaser-close"
              aria-label="Cerrar mensaje de Sara"
              className="absolute -top-2 -left-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-full p-1"
            >
              <X className="h-3 w-3 text-slate-500" />
            </button>
            <button
              data-testid="sara-teaser-open"
              onClick={() => { dismissTeaser(false); setOpen(true); }}
              className="text-left"
            >
              <p className="font-medium text-slate-900 mb-0.5">Hola, soy Sara 👋</p>
              <p className="text-[13px] text-slate-600 leading-snug">Pregúntame precios, horarios o disponibilidad — te respondo al instante.</p>
            </button>
          </div>
        )}
        <div className="relative">
          {/* Anillo de color girando suave — efecto premium */}
          <span
            className="absolute -inset-[5px] rounded-full animate-[spin_7s_linear_infinite]"
            style={{
              background: 'conic-gradient(from 0deg, #fbbf24, #f43f5e, #8b5cf6, #fbbf24)',
              WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3px))',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3px))',
            }}
            aria-hidden="true"
          />
          <button
            onClick={() => { dismissTeaser(false); setOpen(true); }}
            className="relative w-14 h-14 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-transform flex items-center justify-center shrink-0 overflow-hidden ring-4 ring-white/80"
            style={{ background: 'radial-gradient(circle at 35% 30%, #fde68a 0%, #f59e0b 55%, #b45309 100%)' }}
            data-testid="sara-bubble-btn"
            aria-label="Hablar con Sara"
          >
            {/* Brillo superior */}
            <span className="absolute top-1 left-1/2 -translate-x-1/2 w-10 h-5 rounded-[50%] bg-white/40 blur-[3px] pointer-events-none" aria-hidden="true" />
            {/* S premium serif con profundidad */}
            <span className="relative text-white font-bold text-[24px] leading-none select-none"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif', textShadow: '0 2px 4px rgba(0,0,0,0.35)' }}>S</span>
            {/* Destellos */}
            <svg className="absolute top-1.5 right-2.5 w-3 h-3 text-white/90 pointer-events-none" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M6 0 L7.2 4.8 L12 6 L7.2 7.2 L6 12 L4.8 7.2 L0 6 L4.8 4.8 Z" fill="currentColor" />
            </svg>
            <svg className="absolute bottom-2.5 left-2 w-2 h-2 text-white/70 pointer-events-none" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M6 0 L7.2 4.8 L12 6 L7.2 7.2 L6 12 L4.8 7.2 L0 6 L4.8 4.8 Z" fill="currentColor" />
            </svg>
            {/* Punto verde de "en línea" */}
            <span className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-400 rounded-full border-[2.5px] border-white shadow" aria-hidden="true" />
            {humanActive && <span className="absolute top-1 left-1 w-3 h-3 bg-sky-400 rounded-full border-2 border-white" title="Soporte humano disponible" />}
          </button>
        </div>
        {/* Etiqueta flotante en escritorio */}
        <div className="hidden md:block absolute -left-2 bottom-1/2 translate-y-1/2 -translate-x-full mr-3 whitespace-nowrap">
          <div className="bg-slate-900 text-white text-xs font-medium px-3 py-2 rounded-xl shadow-lg">
            ¿Necesitas ayuda? Soy Sara 💬
            <div className="absolute right-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-900 rotate-45" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-[9999] w-[calc(100vw-2rem)] sm:w-[380px] h-[560px] max-h-[calc(100vh-90px)] bg-slate-100 rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
      data-testid="sara-bubble-window"
    >
      <div className="bg-slate-900 text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <div className="relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ring-2 ring-amber-400/60 overflow-hidden"
          style={{ background: 'radial-gradient(circle at 35% 30%, #fde68a 0%, #f59e0b 55%, #b45309 100%)' }}>
          <span className="absolute top-0.5 left-1/2 -translate-x-1/2 w-6 h-3 rounded-[50%] bg-white/40 blur-[2px] pointer-events-none" aria-hidden="true" />
          <span className="relative text-white font-bold text-lg leading-none select-none"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif', textShadow: '0 1px 3px rgba(0,0,0,0.35)' }}>S</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Sara</p>
          <p className="text-[11px] text-emerald-300 truncate flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full inline-block" />
            {humanActive ? 'Soporte humano en la conversación' : isGuest ? 'En línea · responde al instante' : 'En línea'}
            {remaining !== null && remaining !== undefined && ` · ${remaining} mensajes hoy`}
          </p>
        </div>
        <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-slate-700" data-testid="sara-bubble-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      {isGuest && (
        <button
          onClick={() => navigate('/login')}
          className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-[12px] text-amber-800 font-medium flex items-center gap-2 hover:bg-amber-100 flex-shrink-0"
          data-testid="sara-guest-login-link"
        >
          <LogIn className="h-3.5 w-3.5" />
          ¿Quieres reservar? Inicia sesión o regístrate gratis →
        </button>
      )}

      <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {messages.length === 0 && (
          <div className="text-center text-sm text-slate-400 mt-8 px-4">
            <div className="relative w-14 h-14 mx-auto mb-3 rounded-full flex items-center justify-center shadow-lg overflow-hidden"
              style={{ background: 'radial-gradient(circle at 35% 30%, #fde68a 0%, #f59e0b 55%, #b45309 100%)' }}>
              <span className="absolute top-1 left-1/2 -translate-x-1/2 w-8 h-4 rounded-[50%] bg-white/40 blur-[2px] pointer-events-none" aria-hidden="true" />
              <span className="relative text-white font-bold text-2xl leading-none select-none"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif', textShadow: '0 2px 4px rgba(0,0,0,0.35)' }}>S</span>
            </div>
            <p className="font-semibold text-slate-700 mb-1">¡Hola! Soy Sara 👋</p>
            <p>{isGuest
              ? 'Te ayudo con precios, viajes disponibles, a registrarte o a conseguirte un conductor si no hay viaje. Pregúntame lo que sea.'
              : 'Te ayudo con precios, tus reservas, tus viajes o a conseguirte conductor. ¿En qué te ayudo hoy?'}</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap shadow-sm ${roleStyle(m.role)}`} data-testid={`sara-bubble-msg-${m.role}`}>
            {m.role === 'agent' && (
              <p className="text-[10px] font-bold text-emerald-600 flex items-center gap-1 mb-1">
                <Headset className="h-3 w-3" /> Equipo de Soporte
              </p>
            )}
            {m.content}
            {m.meta?.booking_link && (
              <a href={m.meta.booking_link} className="block mt-2 text-center bg-amber-400 text-slate-900 font-semibold rounded-lg py-2 text-xs" data-testid="sara-bubble-booking-link">
                {m.meta.type === 'package_link' ? 'Enviar mi encomienda en la app →' : 'Completar reserva en la app →'}
              </a>
            )}
          </div>
        ))}
        {sending && (
          <div className="bg-white border border-slate-200 rounded-2xl px-4 py-2.5 w-fit shadow-sm">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="border-t bg-white p-2.5 flex gap-2 flex-shrink-0">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Escribe tu mensaje..."
          className="h-10"
          data-testid="sara-bubble-input"
        />
        <Button onClick={send} disabled={sending || !input.trim()} className="h-10 px-3.5 bg-slate-900" data-testid="sara-bubble-send">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
